import {
  BASE_URL,
  DEFAULT_LOCATION,
  PRODUCTS_BY_CATEGORY,
  PRODUCT_DETAILS,
  UPDATE_ZONE
} from 'aster-foundation/core/common/utils/ApiConst';
import { parse as cookieParse } from 'cookie';
import { parse } from 'url';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import CommonMeta from '@common/Component/CommonMeta/CommonMeta';
import {
  getApiAuthHeader,
  getConfigurationActiveValueByName,
  getHomePageJson,
  getIsInstoreApp,
  getQueryStringFromObject,
  getReferralPageForGA,
  getGuestToken,
  getRegionLangTimezoneFromUrl,
} from '@common/Utility/common-utils';
import {
  DEFAULT_SORT_BY,
  EXPRESS_DEFAULT_FLAG,
  HOME_PAGE_JSON_FILE_NAME,
  PLP_PAGE_SIZE,
  SEO_WEB_FOOTER_CONTENT_FLAG,
} from '@common/Utility/constants';
import ProductDetails from 'Epharmacy/ProductDetails/ProductDetails';
import SearchResultPageInner from 'Epharmacy/SearchResult/SearchResultPageInner';
import { SearchResultStyled } from 'Epharmacy/SearchResult/SearchResultStyled';
import Header from '@common/Component/Header/Header';
import HeaderBelow from '@common/Component/HeaderBelow/HeaderBelow';
import TopBar from '@common/Component/TopBar/TopBar';
import EpharmacyContext from '@common/Context/EpharmacyContext';

import FooterQuestions from '@common/Component/FooterQuestions/FooterQuestions';
import NoResultFoundReCarousel from 'Epharmacy/NoResultFoundReCarousel/NoResultFoundReCarousel';
import NoResultfound from 'Epharmacy/SearchResult/NoResultFound';
import { MY_ASTER_CATEGORY, PAGE_NAME } from '@common/Constants/analytics';
import { getLangRegionCodeByPathName } from '@common/i18n/utility';
import { useSetCommonParams } from '@common/Utility/analytics-common';

const Footer = dynamic(() => import('../../../../../Common/Component/Footer/Footer'));
const HereToHelp = dynamic(() => import('../../../../../Common/Component/HereToHelp/HereToHelp'));

export const getParams = (router, params, filterParams) => {
  const reqParams = params;
  let queryData = {};
  if (router?.query?.text) {
    reqParams.text = router?.query?.text;
  }

  if (!params?.sort && DEFAULT_SORT_BY) {
    reqParams.sort = DEFAULT_SORT_BY?.key;
  }

  if (router?.query?.offerType) {
    queryData = { ...queryData, offerType: router?.query?.offerType };
  }

  if (filterParams) {
    queryData = { ...queryData, ...filterParams };
  }
  const queryString = getQueryStringFromObject(queryData);
  if (queryString !== '') {
    reqParams.q = queryString;
  }
  return reqParams;
};

export function getQuery(requestParams) {
  if (requestParams) {
    let queryString = Object.keys(requestParams).length === 0 ? '' : '?';
    for (const key in requestParams) {
      if (requestParams[key] !== '') {
        if (queryString === '?') {
          queryString = `${queryString + key}=${requestParams[key]}`;
        } else {
          queryString = `${queryString}&${key}=${requestParams[key]}`;
        }
      }
    }
    return queryString;
  }
  return '';
}

const fetchDataFromAPI = async (accessToken, deviceId, context, zoneData, filterParams = {}) => {
  let productsResponse = {};
  let params = {
    zone: zoneData,
    page: context?.query?.page ? context?.query?.page - 1 : 0,
    pageSize: PLP_PAGE_SIZE,
  };
  let catId = context?.query?.category;
  if (context?.query?.categories) {
    catId = context?.query?.categories[context?.query?.categories?.length - 1];
  }
  if (EXPRESS_DEFAULT_FLAG) {
    filterParams = { ...filterParams, express_bool: EXPRESS_DEFAULT_FLAG };
  }
  if ((context?.query?.category || context?.query?.categories) && zoneData) {
    params = getParams(context, params, filterParams);
    const {region, lang, timezone} = getRegionLangTimezoneFromUrl(context?.resolvedUrl);
    productsResponse = await fetch(`${PRODUCTS_BY_CATEGORY}/${catId}${getQuery(params)}`, {
      headers: getApiAuthHeader({
        Authorization: `guest-token ${accessToken}`,
        deviceId,
        'x-country-code': region,
        'x-site-time-zone': timezone,
        'x-language': lang,
      }),
    })
      .then((response) => response.json())
      .catch((err) => console.log(err));
  }
  return productsResponse;
};

async function getAccessToken(deviceId) {
  let accessToken = '';
  try {
    accessToken = await getGuestToken(deviceId);
  } catch (e) {
    accessToken = '';
  }
  return accessToken;
}

async function getProductDetails(accessToken, deviceId, context, zoneData, baseUrl = '') {
  let result = {};
  try {
    let param = '';
    const apiEndPoint = `${baseUrl + PRODUCT_DETAILS}?`;

    const urlParams = context?.query?.categories;
    const pIndex = urlParams.indexOf('p');
    const catParams = urlParams?.splice(0, pIndex);
    param += `sku=${urlParams[urlParams?.length - 1]}&`;
    if (context?.query?.categoryId) {
      param += `categoryId=${context?.query?.categoryId}`;
    } else if (catParams?.length > 0) {
      param += `categoryId=${catParams[catParams?.length - 1]}`;
    }
    if (zoneData) {
      param += `zoneIdList=${zoneData}`;
    }
    const productRes = await fetch(apiEndPoint + param, {
      headers: getApiAuthHeader({
        Authorization: `guest-token ${accessToken}`,
        deviceId,
      }),
    });
    result = await productRes.json();
  } catch (e) {
    result = {};
  }
  return result?.data ? result?.data : {};
}

export async function getServerSideProps(context) {
  const cookies = cookieParse(context.req.headers.cookie || '', { decode: true });

  const refPageString = context?.req?.headers?.referer?.split('/');
  const referrerPage = getReferralPageForGA(refPageString);

  const regexPattern = /^\/online-pharmacy\/c\/\d+$/;
  const { req } = context;
  const parsedUrl = parse(req.url, true);
  const pathname = parsedUrl?.pathname;

  const deviceId = uuidv4();
  const accessToken = await getAccessToken(deviceId);
  let facetResponse = {};
  let metaData = {};
  let productDetails = {};
  let isProductPage = false;
  const urlParams = context?.query?.categories;
  const homePageSeoData = await getHomePageJson(HOME_PAGE_JSON_FILE_NAME.WEB);
  let zoneData = cookies?.userZone;

  const selectedRegion = getLangRegionCodeByPathName("region", pathname)?.toUpperCase();
  const selectedLanguage = getLangRegionCodeByPathName("lang", pathname);
  const baseUrl = BASE_URL;
  
  if (typeof cookies?.userZone === 'undefined' || cookies?.userZone === 'null' || !cookies?.userZone) {
    const getAddress = await fetch(baseUrl + DEFAULT_LOCATION(selectedRegion), {
      headers: getApiAuthHeader({
        Authorization: `guest-token ${accessToken}`,
        deviceId,
      }),
    });
    const address = await getAddress?.json();
    const latLong = address?.data?.[0]?.latLong?.split(/\s+/)?.join('')?.split(',');

    const data = {
      latitude: latLong?.[0],
      longitude: latLong?.[1],
    };

    const getZone = await fetch(baseUrl + UPDATE_ZONE, {
      method: 'POST',
      headers: getApiAuthHeader({
        Authorization: `guest-token ${accessToken}`,
        deviceId,
      }),
      body: JSON.stringify(data),
    });
    const getZoneData = await getZone?.json();
    zoneData = getZoneData?.data?.join(',');
  }

  if (urlParams?.find((item) => item === 'p')) {
    isProductPage = true;
    productDetails = await getProductDetails(accessToken, deviceId, context, zoneData, baseUrl);
  } else {
    facetResponse = await fetchDataFromAPI(accessToken, deviceId, context, zoneData);
    const responseMeta = facetResponse?.metaData;
    let title = '';
    let description = responseMeta?.metaDescription;
    if (responseMeta?.metaTitle && responseMeta?.metaTitle !== '') {
      title = responseMeta?.metaTitle;
    } else if (homePageSeoData?.staticPageSeo?.productListingPage?.title) {
      const queryName = facetResponse?.metaData?.canonicalTag?.split("/")?.[5].toLowerCase();
      const queryId = context.query.categories[0];
      title = responseMeta?.name ? responseMeta?.name : '';
      title = String(homePageSeoData.staticPageSeo.productListingPage.title).replace(/{categoryOrbrand}/g, `${title}`);
    }
    if (responseMeta?.metaDescription && responseMeta?.metaDescription !== '') {
      description = responseMeta?.metaDescription;
    } else if (homePageSeoData?.staticPageSeo?.productListingPage?.description) {
      description = responseMeta?.name ? responseMeta?.name : '';
      description = String(homePageSeoData.staticPageSeo.productListingPage.description).replace(
        /{categoryOrbrand}/g,
        `${description}`,
      );
    }
    metaData = {
      title,
      description,
      meta: [
        {
          name: 'title',
          value: title,
        },
        {
          name: 'description',
          value: description,
        },
        {
          name: 'keywords',
          value: responseMeta?.metaKeywords ?? '',
        },
      ],
    };
  }
  return {
    props: {
      productDetails,
      isProductPage,
      queryParam: context.query,
      homePageSeoData,
      facetResponse,
      metaData: metaData ?? {},
      headerData: { search: true, mobileSearch: true },
      referrerPage,
      selectedLanguage
    },
  };
}

const CategoryProducts = (props) => {
  const router = props?.router;
  const appConfigData = useSelector((state) => state?.astercore?.AppConfig?.data?.data);
  const [showFooterContent, setShowFooterContent] = React.useState();
  const [isFirstCalled, setIsFirstCalled] = React.useState(true);
  const [categoryId, setCategoryId] = React.useState();
  const screeName = `${router?.query?.categories?.[0]} category` || `${router?.query?.brands?.[0]} brand` || '-';
  useSetCommonParams({ screen: screeName, page: PAGE_NAME?.PLP, category: MY_ASTER_CATEGORY.AE });
  
  const epharmacyContextValue = React.useMemo(() => ({ epharmacySearch: true, pageName: 'Product Listing Page' }), []);

  const queryParamCategory = React.useMemo(() => {
    const categories = router?.query?.categories;
    if (categories) {
      const catId = categories[categories.length - 1];
      setCategoryId(catId);
      return catId;
    }
  }, [router?.query?.categories]);

  const queryParamBrands = React.useMemo(() => {
    const brands = router?.query?.brands;
    if (brands) {
      return brands[brands.length - 1];
    }
  }, [router?.query?.brands]);

  React.useEffect(() => {
    if (appConfigData) {
      setShowFooterContent(getConfigurationActiveValueByName(appConfigData, SEO_WEB_FOOTER_CONTENT_FLAG));
    }
  }, [appConfigData?.length, showFooterContent]);

  if (props?.isProductPage) {
    return (
      <div className="page_PDP">
        <EpharmacyContext.Provider value={{ epharmacySearch: true, pageName: 'Product Details Page' }}>
          <TopBar screenName={router?.query?.q ? 'Search Result' : 'Product Listing'} pageType='product listing page' />
          <Header
            search={props?.headerData?.search}
            mobileSearch={props?.headerData?.mobileSearch}
            screenName={router?.query?.q ? 'Search Result' : 'Product Listing'}
          />
          <ProductDetails
            productQuery={router?.query?.categories}
            productDetails={Object.keys(props?.productDetails).length > 0 ? props?.productDetails : undefined}
            homePageSeoData={props?.homePageSeoData}
            isFirstCalled={isFirstCalled}
            setIsFirstCalled={setIsFirstCalled}
          />
        </EpharmacyContext.Provider>
        {!getIsInstoreApp() && <HereToHelp />}
        <Footer classCustomName="pdp-mobile-btm-spacing" />
      </div>
    );
  }
  const priceFacetItem = props?.facetResponse?.facetList?.find((item) => item?.key === 'price');
  const priceFacet = { min: priceFacetItem?.min ?? 0, max: priceFacetItem?.max ?? 0 };

  return (
    <>
      {props?.metaData && <CommonMeta metaData={props?.metaData} />}
      <EpharmacyContext.Provider value={epharmacyContextValue}>
        <SearchResultStyled className={`${props?.className} page_plp`}>
          <TopBar 
          screenName={
              router?.query?.categories
                ? router?.query?.categories[0] + ' category'
                : router?.query?.brands
                ? router?.query?.brands[0] + ' brand'
                : '-'
            }
            pageType={PAGE_NAME?.PLP} 
          />
          <Header
            customOuterClass="searchPageHeader"
            search={props?.headerData?.search}
            mobileSearch={props?.headerData?.mobileSearch}
            screenName={
              router?.query?.categories
                ? router?.query?.categories[0] + ' category'
                : router?.query?.brands
                ? router?.query?.brands[0] + ' brand'
                : '-'
            }
            pageType={PAGE_NAME?.PLP}
          />
          <div className="pageContent pb-0">
            <HeaderBelow
              screenName={
                router?.query?.categories
                  ? router?.query?.categories[0] + ' category'
                  : router?.query?.brands
                  ? router?.query?.brands[0] + ' brand'
                  : '-'
              }
              pageType={PAGE_NAME?.PLP}
              hidePointsBox
            />
            {props?.facetResponse?.data?.length > 0 ? (
              <SearchResultPageInner
                {...props}
                facetResponse={props?.facetResponse}
                getParams={getParams}
                queryParamCategory={queryParamCategory}
                queryParamBrands={queryParamBrands}
                isFirstCalled={isFirstCalled}
                setIsFirstCalled={setIsFirstCalled}
                priceFacet={priceFacet}
                referrerPage={props?.referrerPage}
                lang={props?.selectedLanguage}
              />
            ) : (
              <>
                <NoResultfound />
                <NoResultFoundReCarousel referrerPage={props?.referrerPage} facetResponse={props?.facetResponse} />
              </>
            )}
            {showFooterContent === 'true' && <FooterQuestions footerId={categoryId} footerType="Category" />}
            {!getIsInstoreApp() && <HereToHelp customClass="page_plp_hereToHelp" />}
            <Footer
              screenName={
                router?.query?.categories
                  ? router?.query?.categories[0] + ' category'
                  : router?.query?.brands
                  ? router?.query?.brands[0] + ' brand'
                  : '-'
              }
              pageType={PAGE_NAME?.PLP}
              classCustomName="page_plp_footer"
            />
          </div>
        </SearchResultStyled>
      </EpharmacyContext.Provider>
    </>
  );

};

CategoryProducts.propTypes = {
  productDetails: PropTypes.object,
  isProductPage: PropTypes.bool,
  queryParam: PropTypes.object,
  homePageSeoData: PropTypes.object,
  facetResponse: PropTypes.object,
  className: PropTypes.string,
  metaData: PropTypes.object,
  headerData: PropTypes.object,
  referrerPage: PropTypes.string,
  router: PropTypes.object,
};

CategoryProducts.defaultProps = {
  productDetails: {},
  isProductPage: false,
  queryParam: {},
  homePageSeoData: {},
  facetResponse: {},
  className: '',
  metaData: {},
  headerData: {},
  referrerPage: '',
  router: {},
};
export default CategoryProducts;
